"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LocalePill } from "@/components/LanguageSwitcher";
import { useAuth } from "@/components/providers/auth-provider";
import { cn } from "@/lib/utils";
import type { Locale } from "@/lib/i18n";
import { useSignInHref } from "@/lib/use-sign-in-href";
import { IntentLink } from "./IntentLink";
import { AccountAvatar } from "./AccountMenu";
import { eyebrowCase, useEntryText } from "./MorePanel";
import { startFreeHref, type MoreGroup, type NavEntry, type PrimaryEntry } from "./nav-model";
import styles from "./site-header.module.css";

/**
 * `SITE-NAV01`: the phone menu, below 1024 px (the canvas's NavPhone board).
 * The six places as tiles with the line that says what each is, the rest of
 * the More panel as rows, the language, and the account at the bottom where a
 * thumb reaches it: Start free and Log in signed out, "My learning" signed in.
 *
 * It fills the screen under the bar, so the page behind it stops scrolling
 * while it is open.
 */
export function PhoneMenu({
  id,
  locale,
  top,
  primary,
  groups,
  isActive,
  visibleLocales,
  onNavigate,
}: {
  id: string;
  locale: Locale;
  /** The bar's bottom edge, in viewport pixels: the menu starts there. */
  top: number;
  primary: PrimaryEntry[];
  groups: MoreGroup[];
  isActive: (entry: NavEntry) => boolean;
  visibleLocales: Locale[];
  onNavigate: () => void;
}) {
  const t = useTranslations("nav");
  const text = useEntryText();
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  // `RV-20`: Log in comes back to the page the menu was opened on.
  const signInHref = useSignInHref(locale);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const primaryIds = new Set(primary.map((entry) => entry.id));
  const rowGroups = groups
    .map((group) => ({ ...group, entries: group.entries.filter((entry) => !primaryIds.has(entry.id)) }))
    .filter((group) => group.entries.length > 0);

  async function signOut() {
    setSigningOut(true);
    try {
      await logout();
      onNavigate();
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <div
      id={id}
      className={cn("fixed inset-x-0 bottom-0 z-40 flex flex-col bg-neutral-50 lg:hidden", styles.drop)}
      style={{ top }}
    >
      <div className="flex-1 overflow-y-auto overscroll-contain px-4 pb-6 pt-4">
        <nav aria-label={t("primaryLabel")} className="grid grid-cols-2 gap-2.5">
          {primary.map((entry, index) => {
            const Icon = entry.icon;
            const active = isActive(entry);
            const { label, description } = text(entry);
            return (
              <IntentLink
                key={entry.id}
                href={entry.href}
                onClick={onNavigate}
                aria-current={active ? "page" : undefined}
                style={{ animationDelay: `${index * 30}ms` }}
                className={cn(
                  "flex min-h-[100px] flex-col gap-2 rounded-xl border bg-white p-3.5 outline-none transition-colors duration-150 ease-out focus-visible:ring-2 focus-visible:ring-primary",
                  active ? "border-primary bg-primary/5" : "border-neutral-200 active:bg-neutral-50",
                  styles.rise,
                )}
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-primary/10 text-primary">
                  <Icon className="h-[18px] w-[18px]" aria-hidden />
                </span>
                <span className="flex items-center gap-1.5 text-[15px] font-bold text-neutral-900">
                  {label}
                  {entry.key === "learn" ? (
                    <span className="inline-flex h-[18px] items-center rounded-full bg-emerald-50 px-1.5 text-[11px] font-bold text-emerald-700">
                      {t("free")}
                    </span>
                  ) : null}
                </span>
                <span className="text-[13px] leading-snug text-neutral-500">{description}</span>
              </IntentLink>
            );
          })}
        </nav>

        {rowGroups.map((group) => (
          <div key={group.id}>
            <p className={cn("mb-1.5 mt-4 px-1 text-xs font-bold tracking-[0.1em] text-neutral-400", eyebrowCase(locale))}>{t(group.heading)}</p>
            <div className="flex flex-col rounded-xl border border-neutral-200 bg-white px-3">
              {group.entries.map((entry) => {
                const Icon = entry.icon;
                const active = isActive(entry);
                return (
                  <IntentLink
                    key={entry.id}
                    href={entry.href}
                    onClick={onNavigate}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "flex min-h-12 items-center gap-3 border-b border-neutral-100 px-1 text-[15px] outline-none last:border-b-0 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary",
                      active ? "font-semibold text-primary-ink" : "text-neutral-900",
                    )}
                  >
                    <Icon className={cn("h-[18px] w-[18px] shrink-0", active ? "text-primary" : "text-neutral-400")} aria-hidden />
                    {text(entry).label}
                  </IntentLink>
                );
              })}
            </div>
          </div>
        ))}

        {visibleLocales.length > 1 ? (
          <div className="mt-4 flex items-center justify-between px-1">
            <span className="text-sm text-neutral-600">{t("language")}</span>
            <LocalePill locale={locale} visibleLocales={visibleLocales} size="md" />
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-2.5 border-t border-neutral-200 bg-white px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3">
        {isLoading ? (
          <span className="h-12 animate-pulse rounded-xl bg-neutral-100" aria-hidden />
        ) : isAuthenticated && user ? (
          <>
            <Button asChild className="h-12 rounded-xl text-base font-bold">
              <IntentLink href={`/${locale}/dashboard`} onClick={onNavigate}>
                {t("myLearning")}
              </IntentLink>
            </Button>
            <div className="flex items-center gap-3">
              <AccountAvatar user={user} className="h-9 w-9 text-sm" />
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-neutral-900">{user.name}</span>
              <Button
                type="button"
                variant="tertiary"
                disabled={signingOut}
                onClick={() => void signOut()}
                className="h-11 px-3 text-red-700 hover:bg-red-50"
              >
                <LogOut className="h-4 w-4" aria-hidden />
                {t("logout")}
              </Button>
            </div>
          </>
        ) : (
          <>
            <Button asChild className="h-12 rounded-xl text-base font-bold">
              <IntentLink href={startFreeHref(locale)} onClick={onNavigate}>
                {t("startFree")}
              </IntentLink>
            </Button>
            <div className="grid grid-cols-2 gap-2.5">
              <Button asChild variant="outline" className="h-11 rounded-xl font-semibold">
                <IntentLink href={signInHref} onClick={onNavigate}>
                  {t("login")}
                </IntentLink>
              </Button>
              <Button asChild variant="outline" className="h-11 rounded-xl font-semibold">
                <IntentLink href={`/${locale}/courses`} onClick={onNavigate}>
                  {t("exploreCourses")}
                </IntentLink>
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
